import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { Login } from './pages/Login';
import { NavigationBar } from './components/NavigationBar';
import './App.css';
import API_BASE_URL from './config/apiBaseUrl';
import { fetchJson, isUnauthorizedError } from './services/authFetch';
import CompetitorComparison from './components/CompetitorComparison';
import EbayScraper from './components/EbayScraper';
import EbayTopSales from './components/EbayTopSales';
import VendorManagement from './components/VendorManagement';
import MoteurImport from './components/MoteurImport';
import GearboxOpisto from './components/GearboxOpisto';
import Dashboard from './components/Dashboard';
import MarketWatch from './components/MarketWatch';
import LibelleManagement from './components/LibelleManagement';
import { isViewer, VIEWER_ALLOWED_TABS } from './utils/roles';
import fertLogo from './assets/fert-logo-black.png';

const TAB_CONFIG = [
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    component: Dashboard,
    icon: 'DB',
    section: 'Pilotage',
    description: 'Vue executive et arbitrages priorises'
  },
  {
    key: 'market-watch',
    label: 'Veille marche',
    component: MarketWatch,
    icon: 'VM',
    section: 'Pilotage',
    description: 'Monitoring, synchronisation et opportunites'
  },
  {
    key: 'comparison_old',
    label: 'Prix concurrents',
    component: CompetitorComparison,
    icon: 'PC',
    section: 'Analyses',
    description: 'Lecture prix et ecarts concurrentiels'
  },
  {
    key: 'ebay',
    label: 'Analyse ref',
    component: EbayScraper,
    icon: 'AR',
    section: 'Analyses',
    description: 'Recherche ciblee et signaux eBay'
  },
  {
    key: 'ebay-top-sales',
    label: 'Top 50 vendeur',
    component: EbayTopSales,
    icon: 'T5',
    section: 'Analyses',
    description: 'Focus vendeurs et top rotations'
  },
  {
    key: 'moteur',
    label: 'Moteur',
    component: MoteurImport,
    icon: 'MT',
    section: 'Operations',
    description: 'Imports et analyses moteur'
  },
  {
    key: 'boites-vitesses',
    label: 'Boîtes de vitesses',
    component: GearboxOpisto,
    icon: 'BV',
    section: 'Operations',
    description: 'Import et prix Opisto des boîtes'
  },
  {
    key: 'libelles',
    label: 'Gestion libelles',
    component: LibelleManagement,
    icon: 'GL',
    section: 'Operations',
    description: 'Referentiel, nettoyage et imports'
  },
  {
    key: 'vendors',
    label: 'Vendeurs',
    component: VendorManagement,
    icon: 'VD',
    section: 'Operations',
    description: 'Administration du panel vendeur'
  }
];

const fertTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#e61b34',
      dark: '#b80d25',
      light: '#ff5b6f'
    },
    secondary: {
      main: '#13151a'
    },
    background: {
      default: '#f6f7f9',
      paper: '#ffffff'
    },
    text: {
      primary: '#13151a',
      secondary: '#667085'
    },
    success: {
      main: '#15803d'
    },
    warning: {
      main: '#c2410c'
    },
    error: {
      main: '#b42318'
    }
  },
  shape: {
    borderRadius: 18
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h4: {
      fontWeight: 700
    },
    h5: {
      fontWeight: 700
    },
    h6: {
      fontWeight: 700
    },
    button: {
      textTransform: 'none',
      fontWeight: 600
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f6f7f9'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(17, 24, 39, 0.08)',
          boxShadow: '0 20px 45px rgba(17, 24, 39, 0.06)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(17, 24, 39, 0.08)',
          boxShadow: '0 20px 45px rgba(17, 24, 39, 0.06)'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#e61b34',
          height: 3,
          borderRadius: 999
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 50
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8fafc',
          color: '#344054',
          fontWeight: 700
        }
      }
    }
  }
});

function groupTabs(tabs) {
  const groups = new Map();

  tabs.forEach((tab) => {
    if (!groups.has(tab.section)) {
      groups.set(tab.section, []);
    }

    groups.get(tab.section).push(tab);
  });

  return Array.from(groups.entries());
}

function AppContent() {
  const { user } = useAuth();
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const visibleTabs = isViewer(user)
    ? TAB_CONFIG.filter((tab) => VIEWER_ALLOWED_TABS.includes(tab.key))
    : TAB_CONFIG;
  const activeConfig = visibleTabs.find((tab) => tab.key === activeTab) || visibleTabs[0];
  const ActiveComponent = activeConfig?.component;
  const statusOnline = Boolean(status);

  useEffect(() => {
    fetchJson(`${API_BASE_URL}/health`)
      .then((data) => setStatus(data.status))
      .catch((err) => {
        if (isUnauthorizedError(err)) {
          return;
        }

        console.error('Erreur:', err);
        setStatus('Deconnecte');
      });
  }, []);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || 'dashboard');
    }
  }, [activeTab, visibleTabs]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSidebarOpen(false);
  };

  return (
    <div className="app-shell">
      <aside className={`app-sidebar ${sidebarOpen ? 'app-sidebar--open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-logo">
            <img src={fertLogo} alt="Groupe Fert Recyclage" />
          </div>
          <div className="sidebar-brand-copy">
            <span className="sidebar-brand-kicker">Interface Fert Recyclage</span>
            <strong>Pilotage PRE</strong>
          </div>
        </div>


        <div className="sidebar-nav">
          {groupTabs(visibleTabs).map(([section, tabs]) => (
            <div key={section} className="sidebar-nav-group">
              <span className="sidebar-nav-section">{section}</span>
              <div className="sidebar-nav-items">
                {tabs.map((tab) => {
                  const isActive = tab.key === activeTab;

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      className={`sidebar-nav-item ${isActive ? 'sidebar-nav-item--active' : ''}`}
                      onClick={() => handleTabChange(tab.key)}
                    >
                      <span className="sidebar-nav-icon">{tab.icon}</span>
                      <span className="sidebar-nav-copy">
                        <span>{tab.label}</span>
                        <small>{tab.description}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="app-main-shell">
        <NavigationBar
          status={status}
          activeTab={activeConfig}
          onMenuToggle={() => setSidebarOpen((current) => !current)}
        />

        <main className="app-page">
          <section className="app-module-card">
            {ActiveComponent ? <ActiveComponent key={activeConfig.key} /> : null}
          </section>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={fertTheme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={(
                <PrivateRoute>
                  <AppContent />
                </PrivateRoute>
              )}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
